// @ts-strict-ignore
import { attributeListUrlWithAttributeTypePreset } from "@dashboard/attributes/urls";
// 대량구매 설정 (2026-09-01 KST 추가)
import { bulkPurchaseSettingsPath } from "@dashboard/bulkPurchaseSettings/urls";
import { businessInfoSettingsPath } from "@dashboard/businessInfoSettings/urls";
import { channelsListUrl } from "@dashboard/channels/urls";
import { AttributeTypeEnum, PermissionEnum } from "@dashboard/graphql";
import Attributes from "@dashboard/icons/Attributes";
import Channels from "@dashboard/icons/Channels";
import { ConfigurationModelingIcon } from "@dashboard/icons/Modeling";
import PermissionGroups from "@dashboard/icons/PermissionGroups";
import { ConfigurationProductsIcon } from "@dashboard/icons/Products";
import ShippingMethods from "@dashboard/icons/ShippingMethods";
import SiteSettings from "@dashboard/icons/SiteSettings";
import StaffMembers from "@dashboard/icons/StaffMembers";
// 세금 설정 메뉴 재노출 (2026-08-20 KST)
import Taxes from "@dashboard/icons/Taxes";
import Warehouses from "@dashboard/icons/Warehouses";
// 토스페이먼츠 / 배송조회 연동 키 설정 (2026-08-30 KST 추가)
import { deliveryTrackerSettingsPath, tossSettingsPath } from "@dashboard/integrationSettings/urls";
import { sectionNames } from "@dashboard/intl";
// 카카오 채널톡 설정 (2026-10-20 KST 추가)
import { kakaoChannelSettingsPath } from "@dashboard/kakaoChannelSettings/urls";
// 킴스온 이전 (2026-11-02 KST 추가)
import { kimsonImportPath } from "@dashboard/kimsonImport/urls";
import { pageTypeListUrl } from "@dashboard/modelTypes/urls";
import { paymentMethodSettingsPath } from "@dashboard/paymentMethodSettings/urls";
import { permissionGroupListUrl } from "@dashboard/permissionGroups/urls";
// 포인트 설정 (2026-08-25 KST 추가)
import { pointSettingsPath } from "@dashboard/pointSettings/urls";
// 우편번호 API 설정 (2026-08-10 KST 추가)
import { postcodeSettingsPath } from "@dashboard/postcodeSettings/urls";
import { productTypeListUrl } from "@dashboard/productTypes/urls";
import { refundsSettingsPath } from "@dashboard/refundsSettings/urls";
import { shippingZonesListUrl } from "@dashboard/shipping/urls";
// 도서/산간 지역 배송비 (2026-08-24 KST 추가)
import { shippingRegionsPath } from "@dashboard/shippingRegions/urls";
// 기본 배송 설정 (2026-08-24 KST 추가)
import { shippingSettingsPath } from "@dashboard/shippingSettings/urls";
import { siteSettingsUrl } from "@dashboard/siteSettings/urls";
// SNS 로그인 설정 (2026-08-07 KST 추가)
import { snsLoginSettingsPath } from "@dashboard/snsLoginSettings/urls";
import { staffListUrl } from "@dashboard/staff/urls";
import { taxConfigurationListUrl } from "@dashboard/taxes/urls";
// 쿠폰 자동발급 설정 (2026-09-06 KST 추가)
import { voucherAutoIssuePath } from "@dashboard/voucherAutoIssue/urls";
import { warehouseSection } from "@dashboard/warehouses/urls";
import {
  Banknote,
  Building2,
  Coins,
  CreditCard,
  DatabaseZap,
  FileSpreadsheet,
  MapPin,
  MessageCircle,
  PackageSearch,
  Share2,
  Ship,
  TicketPercent,
  Truck,
  Wallet,
} from "lucide-react";
import { type IntlShape } from "react-intl";

import { type MenuSection } from "./types";

// 대분류마다 아이콘 색을 달리해 한눈에 구역이 나뉘게 한다. AdminLTE v4 팔레트에서
// 흰 글씨 대비 4.6:1 이상인 색만 골랐다 (2026-09-29 KST 추가)
const SECTION_COLORS = {
  OLIVE: "#5f7f0f",
  INDIGO: "#6f60ea",
  AMBER: "#a56710",
  STEEL: "#3a4860",
  TEAL: "#12827d",
  SKY: "#127caf",
  VIOLET: "#9553db",
  ORANGE: "#c84e10",
} as const;

export function createConfigurationMenu(intl: IntlShape): MenuSection[] {
  return [
    {
      label: intl.formatMessage({
        id: "ZCUS72",
        defaultMessage: "Product Settings",
        description: "configuration section label for product types and attributes",
      }),
      accentColor: SECTION_COLORS.OLIVE,
      menuItems: [
        {
          description: intl.formatMessage({
            id: "n0RwMK",
            defaultMessage: "Define types of products you sell",
          }),
          icon: <ConfigurationProductsIcon />,
          permissions: [PermissionEnum.MANAGE_PRODUCT_TYPES_AND_ATTRIBUTES],
          title: intl.formatMessage(sectionNames.productTypes),
          url: productTypeListUrl(),
          testId: "configuration-menu-product-types",
        },
        {
          description: intl.formatMessage({
            id: "usPgB+",
            defaultMessage: "Manage attributes used for product types",
            description: "configuration menu item description",
          }),
          icon: <Attributes />,
          permissions: [PermissionEnum.MANAGE_PRODUCT_TYPES_AND_ATTRIBUTES],
          title: intl.formatMessage({
            id: "GTg7rP",
            defaultMessage: "Product attributes",
            description: "configuration menu item title",
          }),
          url: attributeListUrlWithAttributeTypePreset(AttributeTypeEnum.PRODUCT_TYPE),
          testId: "configuration-menu-product-attributes",
        },
      ],
    },
    {
      label: intl.formatMessage({
        id: "Q/8Uby",
        defaultMessage: "Model Settings",
        description: "configuration section label for model types and attributes",
      }),
      accentColor: SECTION_COLORS.INDIGO,
      menuItems: [
        {
          description: intl.formatMessage({
            id: "j4dRq/",
            defaultMessage: "Define types of models you use",
            description: "configuration menu item description",
          }),
          icon: <ConfigurationModelingIcon />,
          permissions: [PermissionEnum.MANAGE_PAGE_TYPES_AND_ATTRIBUTES],
          title: intl.formatMessage(sectionNames.modelTypes),
          url: pageTypeListUrl(),
          testId: "configuration-menu-model-types",
        },
        {
          description: intl.formatMessage({
            id: "fFwHC3",
            defaultMessage: "Manage attributes used for model types",
            description: "configuration menu item description",
          }),
          icon: <Attributes />,
          permissions: [PermissionEnum.MANAGE_PAGE_TYPES_AND_ATTRIBUTES],
          title: intl.formatMessage({
            id: "I0975K",
            defaultMessage: "Model attributes",
            description: "configuration menu item title",
          }),
          url: attributeListUrlWithAttributeTypePreset(AttributeTypeEnum.PAGE_TYPE),
          testId: "configuration-menu-model-attributes",
        },
      ],
    },
    {
      label: intl.formatMessage({
        id: "7GcWC8",
        defaultMessage: "Tax Settings",
        description: "configuration section label",
      }),
      accentColor: SECTION_COLORS.AMBER,
      menuItems: [
        {
          description: intl.formatMessage({
            id: "EIULpW",
            defaultMessage: "Manage how your store charges tax",
          }),
          icon: <Taxes />,
          title: intl.formatMessage(sectionNames.taxes),
          url: taxConfigurationListUrl(),
          testId: "configuration-menu-taxes",
        },
      ],
    },
    {
      label: intl.formatMessage({
        id: "UN+yTt",
        defaultMessage: "Staff Settings",
      }),
      accentColor: SECTION_COLORS.STEEL,
      menuItems: [
        {
          description: intl.formatMessage({
            id: "RQUkVW",
            defaultMessage: "Manage your employees and their permissions",
          }),
          icon: <StaffMembers />,
          permissions: [PermissionEnum.MANAGE_STAFF],
          title: intl.formatMessage(sectionNames.staff),
          url: staffListUrl(),
          testId: "configuration-menu-staff",
        },
        {
          description: intl.formatMessage({
            id: "ivJ1qt",
            defaultMessage: "Manage your permission groups and their permissions",
          }),
          icon: <PermissionGroups />,
          permissions: [PermissionEnum.MANAGE_STAFF],
          title: intl.formatMessage(sectionNames.permissionGroups),
          url: permissionGroupListUrl(),
          testId: "configuration-menu-permission-groups",
        },
      ],
    },
    {
      label: intl.formatMessage({
        id: "gTr0qE",
        defaultMessage: "Shipping Settings",
      }),
      accentColor: SECTION_COLORS.TEAL,
      menuItems: [
        // 기본 배송 설정 (2026-08-24 KST 추가)
        {
          description: intl.formatMessage({
            id: "GylSYM",
            defaultMessage: "배송비 결제/유형과 제주·도서산간 추가 배송비를 관리합니다",
          }),
          icon: <Truck />,
          permissions: [PermissionEnum.MANAGE_SETTINGS],
          title: intl.formatMessage({
            id: "Ncr+JO",
            defaultMessage: "기본 배송 설정",
          }),
          url: shippingSettingsPath,
          testId: "configuration-menu-shipping-settings",
        },
        // 도서/산간 지역 배송비 (2026-08-24 KST 추가)
        {
          description: intl.formatMessage({
            id: "PEzryT",
            defaultMessage: "우편번호별 추가 배송비를 엑셀로 등록하고 관리합니다",
          }),
          icon: <Ship />,
          permissions: [PermissionEnum.MANAGE_SETTINGS],
          title: intl.formatMessage({
            id: "cFbdSL",
            defaultMessage: "도서/산간 지역 배송비",
          }),
          url: shippingRegionsPath,
          testId: "configuration-menu-shipping-regions",
        },
        {
          description: intl.formatMessage({
            id: "zxs6G3",
            defaultMessage: "Manage how you ship out orders",
          }),
          icon: <ShippingMethods />,
          permissions: [PermissionEnum.MANAGE_SHIPPING],
          title: intl.formatMessage(sectionNames.shipping),
          url: shippingZonesListUrl(),
          testId: "configurationMenuShipping",
        },
        {
          description: intl.formatMessage({
            id: "5RmuD+",
            defaultMessage: "Manage and update your warehouse information",
          }),
          icon: <Warehouses />,
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          title: intl.formatMessage(sectionNames.warehouses),
          url: warehouseSection,
          testId: "configuration-menu-warehouses",
        },
        // 배송조회 연동 키 (2026-08-30 KST 추가)
        // 기타 섹션에서 배송 설정 섹션으로 이동 (2026-10-07 KST 수정)
        {
          description: intl.formatMessage({
            id: "Nx1MjY",
            defaultMessage: "운송장 배송조회에 사용할 딜리버리 트래커 키를 관리합니다",
          }),
          icon: <PackageSearch />,
          permissions: [PermissionEnum.MANAGE_SETTINGS],
          title: intl.formatMessage({
            id: "IrcC6v",
            defaultMessage: "배송조회",
          }),
          url: deliveryTrackerSettingsPath,
          testId: "configuration-menu-delivery-tracker-settings",
        },
      ],
    },
    // 결제 설정 — 기타 분류에 흩어져 있던 결제 관련 항목을 묶는다 (2026-09-07 KST 추가)
    // 주문(체크아웃) 설정 분류를 없애고 그 항목들을 여기로 합쳤다 (2026-09-08 KST)
    {
      label: intl.formatMessage({
        id: "K0p7L1",
        defaultMessage: "결제 설정",
      }),
      accentColor: SECTION_COLORS.SKY,
      menuItems: [
        // 결제수단 노출 설정 (2026-08-06 KST 추가)
        // 무통장입금 계좌 메뉴를 합쳐 '결제/무통장 설정' 하나로 만들었다 (2026-10-18 KST 수정)
        {
          description: intl.formatMessage({
            id: "c4C7N/",
            defaultMessage: "결제수단 노출 순서·명칭과 무통장입금 계좌를 관리합니다",
          }),
          icon: <Wallet />,
          permissions: [PermissionEnum.MANAGE_SETTINGS],
          title: intl.formatMessage({
            id: "T/FUsD",
            defaultMessage: "결제/무통장 설정",
          }),
          url: paymentMethodSettingsPath,
          testId: "configuration-menu-payment-method-settings",
        },
        // 토스페이먼츠 연동 키 (2026-08-30 KST 추가)
        {
          description: intl.formatMessage({
            id: "IkEkiS",
            defaultMessage: "토스페이먼츠 결제에 사용할 연동 키를 관리합니다",
          }),
          icon: <Banknote />,
          permissions: [PermissionEnum.MANAGE_SETTINGS],
          title: intl.formatMessage({
            id: "I1gVDR",
            defaultMessage: "토스페이먼츠",
          }),
          url: tossSettingsPath,
          testId: "configuration-menu-toss-settings",
        },
        // 우편번호 API 설정 (2026-08-10 KST 추가)
        {
          description: intl.formatMessage({
            id: "UzpzDJ",
            defaultMessage: "주소 검색에 사용할 우편번호 API를 선택합니다 (다음/행안부)",
          }),
          icon: <MapPin />,
          permissions: [PermissionEnum.MANAGE_SETTINGS],
          title: intl.formatMessage({
            id: "51gvhT",
            defaultMessage: "우편번호 API",
          }),
          url: postcodeSettingsPath,
          testId: "configuration-menu-postcode-settings",
        },
        // 대량구매 설정 (2026-09-01 KST 추가, 2026-09-01 KST 이름 변경)
        {
          description: intl.formatMessage({
            id: "A9jrFU",
            defaultMessage: "대량 구매 탭 노출 여부와 배송지 목록 양식을 관리합니다",
          }),
          icon: <FileSpreadsheet />,
          permissions: [PermissionEnum.MANAGE_SETTINGS],
          title: intl.formatMessage({
            id: "LXsOF3",
            defaultMessage: "대량구매 설정",
          }),
          url: bulkPurchaseSettingsPath,
          testId: "configuration-menu-bulk-purchase-settings",
        },
      ],
    },
    {
      label: intl.formatMessage({
        id: "MWSacl",
        defaultMessage: "Multichannel",
      }),
      accentColor: SECTION_COLORS.VIOLET,
      menuItems: [
        {
          description: intl.formatMessage({
            id: "8vJCJ4",
            defaultMessage: "Define and manage your sales channels",
          }),
          icon: <Channels />,
          permissions: [PermissionEnum.MANAGE_CHANNELS],
          title: intl.formatMessage(sectionNames.channels),
          url: channelsListUrl(),
          testId: "configuration-menu-channels",
        },
      ],
    },
    {
      label: intl.formatMessage({
        id: "YZl6cv",
        defaultMessage: "Miscellaneous",
      }),
      accentColor: SECTION_COLORS.ORANGE,
      menuItems: [
        {
          description: intl.formatMessage({
            id: "5BajZK",
            defaultMessage: "View and update your site settings",
          }),
          icon: <SiteSettings />,
          permissions: [PermissionEnum.MANAGE_SETTINGS],
          title: intl.formatMessage(sectionNames.siteSettings),
          url: siteSettingsUrl(),
          testId: "configuration-menu-site-settings",
        },
        {
          description: intl.formatMessage({
            id: "3fKAKV",
            defaultMessage: "Configure refunds and returns behavior",
          }),
          icon: <CreditCard />,
          permissions: [PermissionEnum.MANAGE_SETTINGS],
          title: intl.formatMessage(sectionNames.refundsSettings),
          url: refundsSettingsPath,
          testId: "configuration-menu-refunds-settings",
        },
        // 사업자 정보 설정 (2026-08-07 KST 추가)
        {
          description: intl.formatMessage({
            id: "IF0gNs",
            defaultMessage: "스토어 푸터에 표기할 사업자 정보를 관리합니다",
          }),
          icon: <Building2 />,
          permissions: [PermissionEnum.MANAGE_SETTINGS],
          title: intl.formatMessage({
            id: "NGrNpA",
            defaultMessage: "사업자 정보",
          }),
          url: businessInfoSettingsPath,
          testId: "configuration-menu-business-info-settings",
        },
        // SNS 로그인 설정 (2026-08-07 KST 추가)
        {
          description: intl.formatMessage({
            id: "tYdp0a",
            defaultMessage: "카카오·네이버 로그인 연동 정보를 관리합니다",
          }),
          icon: <Share2 />,
          permissions: [PermissionEnum.MANAGE_PLUGINS],
          title: intl.formatMessage({
            id: "sN7k/Y",
            defaultMessage: "SNS 로그인",
          }),
          url: snsLoginSettingsPath,
          testId: "configuration-menu-sns-login-settings",
        },
        // 카카오 채널톡 설정 (2026-10-20 KST 추가)
        {
          description: intl.formatMessage({
            id: "QP8JFv",
            defaultMessage: "스토어 문의하기 버튼에 연결할 카카오톡 채널 정보를 관리합니다",
          }),
          icon: <MessageCircle />,
          permissions: [PermissionEnum.MANAGE_SETTINGS],
          title: intl.formatMessage({
            id: "XWtGgu",
            defaultMessage: "카카오 채널톡",
          }),
          url: kakaoChannelSettingsPath,
          testId: "configuration-menu-kakao-channel-settings",
        },
        // 포인트 설정 (2026-08-25 KST 추가)
        {
          description: intl.formatMessage({
            id: "tf+6CM",
            defaultMessage: "포인트 적립 지급 조건과 사용 제한을 관리합니다",
          }),
          icon: <Coins />,
          permissions: [PermissionEnum.MANAGE_SETTINGS],
          title: intl.formatMessage({
            id: "fppSTl",
            defaultMessage: "포인트 설정",
          }),
          url: pointSettingsPath,
          testId: "configuration-menu-point-settings",
        },
        // 쿠폰 자동발급 설정 (2026-09-06 KST 추가)
        {
          description: intl.formatMessage({
            id: "wyTGZM",
            defaultMessage: "신규가입·첫 구매 시 쿠폰을 자동으로 발급합니다",
          }),
          icon: <TicketPercent />,
          // 쿠폰 목록을 읽고(할인) 설정을 shop metadata 에 저장하므로(설정) 둘 다 필요하다
          permissions: [PermissionEnum.MANAGE_SETTINGS, PermissionEnum.MANAGE_DISCOUNTS],
          requireAllPermissions: true,
          title: intl.formatMessage({
            id: "kb10tG",
            defaultMessage: "쿠폰 자동발급 설정",
          }),
          url: voucherAutoIssuePath,
          testId: "configuration-menu-voucher-auto-issue",
        },
        // 킴스온 이전 (2026-11-02 KST 추가)
        // 실제 사용은 백엔드가 최고 관리자에게만 허용한다. 메뉴는 회원·직원 관리 권한이 모두
        // 있는 관리자에게만 보인다 (최고 관리자는 모든 권한을 가진다).
        {
          description: intl.formatMessage({
            id: "fG/toa",
            defaultMessage: "킴스온 DB 덤프로 회원 정보를 옮깁니다",
          }),
          icon: <DatabaseZap />,
          permissions: [PermissionEnum.MANAGE_USERS, PermissionEnum.MANAGE_STAFF],
          requireAllPermissions: true,
          title: intl.formatMessage({
            id: "caxPd4",
            defaultMessage: "킴스온 이전",
          }),
          url: kimsonImportPath,
          testId: "configuration-menu-kimson-import",
        },
      ],
    },
  ];
}
